import React, { memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import betslip from '@gin-betting/betslip';
import core, { GQL, View } from '@gin-betting/core';
import { OrderedSet } from 'immutable';
import { createSelector, createStructuredSelector } from 'reselect';

import { ACTIVE_MARKET_STATUSES } from 'app-constants';
import { useMemoSelector } from 'hooks';
import ui from 'modules/ui';
import UserDevice from 'services/user-device';
import { getMatchesByIds } from 'utils';

const {
    utils: { getFiltersFromLocation, splitOddPath },
    services: { stringify, getHistory },
    hooks: { useDeepMemo },
    constants: { idPathDivider },
    entities: {
        normalizeEntity,
        actions: { storeNormalizedEntities },
        selectors: { selectActiveOddIds },
    },
} = core;

const {
    actions: {
        addBetslipOdd,
        deleteBetslipOdds,
        changeBetslipMode,
        choseBetslipBetId,
    },
    utils: { getNextModeBySize },
    selectors: { selectInitialBetslipBet, selectChosenBetslipBetId },
} = betslip;

const {
    actions: { openSidebar },
} = ui;

const RestoreBetslipFromURL: React.FC = () => {
    const dispatch = useDispatch();
    const [isRequested, setIsRequested] = useState(false);

    const [{ oddIds, mode }, setRestoreParams] = useState(() => {
        const {
            betslipOdds = [],
            betType = GQL.BetType.SINGLE,
            ...restFilters
        } = getFiltersFromLocation();

        const history = getHistory();

        history.replace({
            ...history.location,
            search: stringify(restFilters),
            hash: window.location.hash,
        });

        return {
            oddIds: betslipOdds.map((id: string) =>
                id.replaceAll('/', idPathDivider)
            ),
            mode: betType,
        };
    });

    const { betslipBetId, activeOddIds, chosenBetslipBetId } = useMemoSelector(
        selectProps,
        {
            oddIds: isRequested ? oddIds : [],
        }
    );

    const isMobile = Boolean(UserDevice.getInstance()?.isDeviceMobile());

    const memActiveOddIds = useDeepMemo(activeOddIds);
    const activeMode =
        memActiveOddIds.length === oddIds.length
            ? mode
            : getNextModeBySize({
                  nextBetslipOddIds: OrderedSet(memActiveOddIds),
                  mode,
              });

    const memVariables = useDeepMemo(
        oddIds.reduce(
            (
                acc: { marketIds: Set<string>; sportEventsIds: Set<string> },
                id
            ) => {
                const ids = splitOddPath(id);

                if (!ids) return acc;

                acc.marketIds.add(ids.marketId);
                acc.sportEventsIds.add(ids.matchId);

                return acc;
            },
            {
                marketIds: new Set<string>(),
                sportEventsIds: new Set<string>(),
            }
        )
    );

    useEffect(() => {
        const { sportEventsIds, marketIds } = memVariables;

        if (sportEventsIds.size === 0 || marketIds.size === 0) return;

        getMatchesByIds({
            sportEventsIds: Array.from(sportEventsIds),
            marketStatuses: ACTIVE_MARKET_STATUSES,
            marketIds: Array.from(marketIds),
        })
            .then(({ data }) => {
                const normalized = normalizeEntity(data) as View.Normalized;

                dispatch(
                    storeNormalizedEntities(normalized, {
                        isTopMarkets: false,
                    })
                );

                setIsRequested(true);
            })
            .catch(error => {
                console.error(error);
            });
    }, [dispatch, memVariables]);

    useEffect(() => {
        if (!betslipBetId || !memActiveOddIds.length) return;

        setRestoreParams({
            oddIds: [],
            mode: GQL.BetType.SINGLE,
        });

        dispatch(deleteBetslipOdds({ betslipBetId }));

        memActiveOddIds.forEach(oddId => {
            dispatch(
                addBetslipOdd({
                    betslipBetId,
                    oddId,
                })
            );
        });

        dispatch(
            changeBetslipMode({
                betslipBetId,
                mode: activeMode,
            })
        );

        if (isMobile) {
            if (betslipBetId !== chosenBetslipBetId) {
                dispatch(choseBetslipBetId({ betslipBetId }));
            }

            dispatch(openSidebar());
        }
    }, [
        dispatch,
        memActiveOddIds,
        betslipBetId,
        chosenBetslipBetId,
        activeMode,
    ]);

    return null;
};

const selectInitialBetslipBetId = createSelector(
    selectInitialBetslipBet,
    betslipBet => betslipBet?.id
);

const selectProps = createStructuredSelector({
    betslipBetId: selectInitialBetslipBetId,
    chosenBetslipBetId: selectChosenBetslipBetId,
    activeOddIds: selectActiveOddIds,
});

export default memo(RestoreBetslipFromURL);
